// InvoicePage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiCall } from '../../utils/api';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaFilePdf, FaInbox } from 'react-icons/fa';
import PageHeader from '../../components/ui/page-header';
import { COLORS } from '../../theme';
import { Card } from '../../components/ui/card';
import { Table, THead, TBody, Th, Tr } from '../../components/ui/table';
import { Badge } from '../../components/ui/status-badge';

// ---- Same company details as the ledger ----
const companyDetails = {
    name: "Paywon Private Limited",
    cin: "U61101AS2026PTC030542",
    gst: "18AARCP1154C1ZG",
    contact: "+916900023991",
    email: "support@paywon.in",
    address: "Niz Dalgaon, Darrang, Assam, PIN 784116"
};

// ---- Map API orderType string → numeric category ----
const getCategoryFromOrderType = (orderType) => {
    const t = String(orderType || '').toLowerCase();
    if (t.includes('recharge') || t.includes('dth')) return 2;
    if (t.includes('bill')) return 3;
    if (t.includes('payment')) return 1;
    return 0;
};

const getServiceDescription = (orderType) => {
    const t = String(orderType || '').toLowerCase();
    if (t.includes('recharge') || t.includes('dth')) return 'Mobile Recharge & DTH Recharge';
    if (t.includes('bill')) return 'Bill Payment Service';
    if (t.includes('payment')) return 'Payment Service';
    return orderType || 'Service';
};

const getHSNCode = (category) => (category === 2 ? '998413' : '-');

const getGSTRates = (category) => {
    if (category === 2) return { cgst: 9, sgst: 9, igst: 0 };
    return { cgst: 0, sgst: 0, igst: 0 };
};

const numberToWords = (num) => {
    num = Math.round(Number(num) * 100) / 100;
    const parts = num.toFixed(2).split('.');
    let whole = parseInt(parts[0], 10);
    const decimal = parseInt(parts[1] || '0', 10);
    if (num === 0) return 'Rupees Zero Only';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
        'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const two = (n) => (n < 20 ? ones[n] : `${tens[Math.floor(n / 10)]}${n % 10 ? ' ' + ones[n % 10] : ''}`);

    let text = '';
    if (whole >= 10000000) { text += two(Math.floor(whole / 10000000)) + ' Crore '; whole %= 10000000; }
    if (whole >= 100000) { text += two(Math.floor(whole / 100000)) + ' Lakh '; whole %= 100000; }
    if (whole >= 1000) { text += two(Math.floor(whole / 1000)) + ' Thousand '; whole %= 1000; }
    if (whole >= 100) { text += two(Math.floor(whole / 100)) + ' Hundred '; whole %= 100; }
    if (whole > 0) text += two(whole) + ' ';
    text = text.trim();
    if (decimal > 0) text += ` and ${String(decimal).padStart(2, '0')}/100`;
    return `Rupees ${text} Only`;
};

const formatCurrency = (n) => parseFloat(n || 0).toFixed(2);

function InvoicePage() {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const order_id = params.get('order_id');

    const fetchInvoice = async () => {
        if (!order_id) { toast.error('Missing order id'); return; }
        setLoading(true);
        try {
            const result = await apiCall('order-details', { order_id }, true);
            if (result.status === true && result.data) {
                setOrder(result.data);
            } else {
                toast.error(result.message || 'Failed to fetch invoice');
                setOrder(null);
            }
        } catch {
            toast.error('Network error. Please try again.');
            setOrder(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoice();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order_id]);

    const hasOrder = !!order;
    const category = order ? getCategoryFromOrderType(order.orderType) : 0;
    const amount = order ? parseFloat(order.amount || 0) : 0;
    const gstRates = getGSTRates(category);
    const hsnCode = getHSNCode(category);
    const serviceDesc = getServiceDescription(order?.orderType);

    let taxable = 0, cgstAmt = 0, sgstAmt = 0, totalAmt = 0;
    if (hasOrder) {
        if (category === 2) {
            const rate = (gstRates.cgst + gstRates.sgst) / 100;
            taxable = amount / (1 + rate);
            cgstAmt = taxable * (gstRates.cgst / 100);
            sgstAmt = taxable * (gstRates.sgst / 100);
            totalAmt = amount;
        } else {
            taxable = amount;
            cgstAmt = taxable * (gstRates.cgst / 100);
            sgstAmt = taxable * (gstRates.sgst / 100);
            totalAmt = taxable + cgstAmt + sgstAmt;
        }
    }

    const invoiceDate = order?.orderDate || 'N/A';
    const operator = order?.operatorName || 'N/A';
    const provider = order?.providerName || 'N/A';
    const cn = order?.cn || 'N/A';
    const rrn = order?.txn || '—';
    const statusId = order?.statusId || 'N/A';

    const customer = {
        name: order?.retailerName || 'N/A',
        mobile: order?.retailerMobile || 'N/A',
        email: order?.retailerEmail || 'N/A',
        address: 'N/A',
        aadhaar_verified: order?.retailerKycVerification === '1' ? 'Verified' : 'No',
    };

    const getStatusColor = (status) => {
        const colors = { 'Success': 'emerald', 'Failed': 'rose', 'Pending': 'amber', 'Initiated': 'brand' };
        return colors[status] || 'slate';
    };

    // ===== PDF — Landscape A4, ledger-style header, dynamic boxes =====
    const downloadPDF = () => {
        if (!hasOrder) { toast.error('No invoice to download'); return; }

        const doc = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 12;

        // ===== Header Band (identical to ledger — page 1 only) =====
        doc.setFillColor(30, 41, 59);
        doc.rect(0, 0, pageWidth, 26, 'F');

        doc.setFontSize(16);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.text(companyDetails.name, margin, 11);

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(203, 213, 225);
        doc.text(`CIN: ${companyDetails.cin}  |  GST: ${companyDetails.gst}`, margin, 17);
        doc.text(`${companyDetails.contact}  |  ${companyDetails.email}`, margin, 21.5);
        doc.text(companyDetails.address, pageWidth - margin, 21.5, { align: 'right' });

        // ===== Title Row =====
        let y = 36;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.setTextColor(15, 23, 42);
        doc.text('TAX INVOICE', margin, y);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.text(
            `Order ID: ${order.orderId || 'N/A'}   |   Date: ${invoiceDate}`,
            pageWidth - margin,
            y,
            { align: 'right' }
        );

        // ===== Bill To + Invoice Details — dynamic height =====
        y += 5;
        const boxTop = y;
        const halfGap = 6;
        const boxW = (pageWidth - margin * 2 - halfGap) / 2;
        const innerW = boxW - 10;

        // Build content arrays first
        const billToLines = [
            `Name: ${customer.name}`,
            `Mobile: ${customer.mobile}`,
            `Email: ${customer.email}`,
            `KYC: ${customer.aadhaar_verified}`,
        ];
        const invoiceLines = [
            `Consumer No: ${cn}`,
            `TXN ID: ${rrn}`,
            `Provider: ${provider}`,
            `Status: ${statusId}`,
        ];

        // Wrap long values so they don't run past the box edge
        const wrap = (lines) => lines.flatMap((ln) => doc.splitTextToSize(ln, innerW));
        const leftLines = wrap(billToLines);
        const rightLines = wrap(invoiceLines);

        // Dynamic height: header offset + (rows-1)*lineHeight + bottom pad
        const HEADER_OFFSET = 14;
        const LINE_HEIGHT = 5;
        const BOTTOM_PAD = 4;
        const neededHeight = (rows) =>
            HEADER_OFFSET + Math.max(0, rows.length - 1) * LINE_HEIGHT + BOTTOM_PAD;
        const boxHeight = Math.max(neededHeight(leftLines), neededHeight(rightLines));

        // Draw boxes
        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, boxTop, boxW, boxHeight, 2, 2, 'FD');
        doc.roundedRect(margin + boxW + halfGap, boxTop, boxW, boxHeight, 2, 2, 'FD');

        // ---- Left box: BILL TO ----
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'bold');
        doc.text('BILL TO', margin + 5, boxTop + 7);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(9);
        let lY = boxTop + HEADER_OFFSET;
        leftLines.forEach((line) => {
            doc.text(line, margin + 5, lY);
            lY += LINE_HEIGHT;
        });

        // ---- Right box: INVOICE DETAILS ----
        const rx = margin + boxW + halfGap + 5;
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'bold');
        doc.text('INVOICE DETAILS', rx, boxTop + 7);

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(9);
        let rY = boxTop + HEADER_OFFSET;
        rightLines.forEach((line) => {
            doc.text(line, rx, rY);
            rY += LINE_HEIGHT;
        });

        // ===== Items Table (full width) =====
        const tableRows = [[
            '1',
            hsnCode,
            '1',
            '—',
            `${serviceDesc} (${operator})`,
            formatCurrency(amount),
            formatCurrency(amount),
            '0.00',
            formatCurrency(taxable),
            cgstAmt ? gstRates.cgst.toFixed(3) : '0.000',
            sgstAmt ? gstRates.sgst.toFixed(3) : '0.000',
            '0.000',
            formatCurrency(totalAmt),
        ]];

        const tableWidth = pageWidth - margin * 2;

        doc.autoTable({
            startY: boxTop + boxHeight + 6,
            head: [[
                'Sl No.', 'HSN', 'Qty', 'Svc Code', 'Description',
                'Unit Price', 'Amount', 'Discount', 'Taxable',
                'CGST %', 'SGST %', 'IGST %', 'Total'
            ]],
            body: tableRows,
            foot: [['', '', '', '', 'Grand Total', '', '', '', '', '', '', '', formatCurrency(totalAmt)]],
            theme: 'grid',
            margin: { left: margin, right: margin },
            tableWidth: tableWidth,
            styles: {
                fontSize: 8,
                cellPadding: 2.5,
                lineColor: [226, 232, 240],
                lineWidth: 0.2,
                textColor: [30, 41, 59],
            },
            headStyles: {
                fillColor: [30, 41, 59],
                textColor: [255, 255, 255],
                fontSize: 8,
                fontStyle: 'bold',
                halign: 'center',
            },
            footStyles: {
                fillColor: [241, 245, 249],
                textColor: [15, 23, 42],
                fontStyle: 'bold',
                halign: 'right',
                fontSize: 8,
            },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: '4%', halign: 'center' },
                1: { cellWidth: '6%', halign: 'center' },
                2: { cellWidth: '4%', halign: 'center' },
                3: { cellWidth: '7%', halign: 'center' },
                4: { cellWidth: '24%' },
                5: { cellWidth: '8%', halign: 'right' },
                6: { cellWidth: '8%', halign: 'right' },
                7: { cellWidth: '8%', halign: 'right' },
                8: { cellWidth: '8%', halign: 'right' },
                9: { cellWidth: '5%', halign: 'right' },
                10: { cellWidth: '5%', halign: 'right' },
                11: { cellWidth: '5%', halign: 'right' },
                12: { cellWidth: '8%', halign: 'right', fontStyle: 'bold' },
            },
        });

        // ===== Amount in Words + Remarks =====
        const summaryTop = doc.lastAutoTable.finalY + 8;
        doc.setDrawColor(226, 232, 240);
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(margin, summaryTop, pageWidth - margin * 2, 22, 2, 2, 'FD');

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139);
        doc.text('AMOUNT IN WORDS', margin + 5, summaryTop + 6);
        doc.text('REMARKS', pageWidth - margin - 80, summaryTop + 6);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(15, 23, 42);
        doc.text(numberToWords(totalAmt), margin + 5, summaryTop + 13);
        doc.text(operator, pageWidth - margin - 80, summaryTop + 13);

        // ===== Signature =====
        const sigY = summaryTop + 32;
        if (sigY < pageHeight - 30) {
            doc.setDrawColor(226, 232, 240);
            doc.line(pageWidth - margin - 70, sigY, pageWidth - margin, sigY);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(15, 23, 42);
            doc.text(companyDetails.name, pageWidth - margin, sigY + 5, { align: 'right' });
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(71, 85, 105);
            doc.text('Authorised Signatory', pageWidth - margin, sigY + 10, { align: 'right' });
        }

        // ===== Footer =====
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text(`Generated on ${new Date().toLocaleString()}`, margin, pageHeight - 7);
        doc.text('This is a computer generated invoice.', pageWidth - margin, pageHeight - 7, { align: 'right' });

        doc.save(`invoice_${order.orderId || 'order'}.pdf`);
        toast.success('Invoice PDF downloaded!');
    };

    // ===== Render =====
    return (
        <>
            <PageHeader
                title={`Tax Invoice #${order_id || ''}`}
                description="View and download GST tax invoice"
            />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <div className="flex items-center gap-3">
                    {loading && (
                        <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                            <svg className="animate-spin h-4 w-4 mr-2 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Loading...
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={downloadPDF}
                        disabled={!hasOrder}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        style={{ backgroundColor: COLORS.brand }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1d4ed8')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.brand)}
                    >
                        <FaFilePdf /> Export PDF
                    </button>
                </div>
            </div>

            {!hasOrder && !loading ? (
                <Card className="py-16">
                    <div className="flex flex-col items-center text-slate-400">
                        <FaInbox className="w-12 h-12 mb-3 text-slate-300" />
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No order found</p>
                        <p className="text-xs mt-1">The requested order could not be located in our records.</p>
                    </div>
                </Card>
            ) : hasOrder && (
                <>
                    {/* Company header */}
                    <Card className="mb-6 overflow-hidden">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 px-6 py-4 border-b border-slate-100 dark:border-slate-800">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">{companyDetails.name}</h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{companyDetails.address}</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    GSTIN: <span className="font-medium text-slate-700 dark:text-slate-300">{companyDetails.gst}</span>
                                    {'  |  '}CIN: <span className="font-medium text-slate-700 dark:text-slate-300">{companyDetails.cin}</span>
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    Tel: {companyDetails.contact} · {companyDetails.email}
                                </p>
                            </div>
                            <div className="md:text-right">
                                <p className="text-sm font-bold text-slate-900 dark:text-slate-100 uppercase tracking-wide">Tax Invoice</p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Date: {invoiceDate}</p>
                                <div className="mt-2 flex md:justify-end">
                                    <Badge tone={getStatusColor(statusId)}>{statusId}</Badge>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Bill To + Invoice Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                        <Card className="p-4 border-l-4 border-l-brand-500">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Bill To</p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{customer.name}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Mobile: {customer.mobile}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">Email: {customer.email}</p>
                            <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 flex items-center gap-2">
                                KYC:
                                <Badge tone={customer.aadhaar_verified === 'Verified' ? 'emerald' : 'slate'}>
                                    {customer.aadhaar_verified}
                                </Badge>
                            </p>
                        </Card>

                        <Card className="p-4">
                            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Invoice Details</p>
                            <div className="space-y-1.5">
                                {[
                                    ['Consumer Number', cn],
                                    ['TXN ID', rrn],
                                    ['Provider', provider],
                                    ['Status', statusId],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex justify-between text-xs border-b border-dashed border-slate-100 dark:border-slate-800 pb-1">
                                        <span className="text-slate-500 dark:text-slate-400">{k}</span>
                                        <span className="font-medium text-slate-900 dark:text-slate-100 text-right ml-3 break-all">{v || '—'}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {/* Items table */}
                    <Card className="overflow-hidden mb-6">
                        <div className="overflow-x-auto">
                            <Table>
                                <THead>
                                    <tr>
                                        <Th>Sl No.</Th>
                                        <Th>HSN</Th>
                                        <Th>Qty</Th>
                                        <Th>Svc Code</Th>
                                        <Th>Description</Th>
                                        <Th align="right">Unit Price</Th>
                                        <Th align="right">Amount</Th>
                                        <Th align="right">Discount</Th>
                                        <Th align="right">Taxable</Th>
                                        <Th align="right">CGST %</Th>
                                        <Th align="right">SGST %</Th>
                                        <Th align="right">IGST %</Th>
                                        <Th align="right">Total</Th>
                                    </tr>
                                </THead>
                                <TBody>
                                    <Tr>
                                        <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">1</td>
                                        <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">{hsnCode}</td>
                                        <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">1</td>
                                        <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">—</td>
                                        <td className="px-3 py-3 text-sm text-slate-900 dark:text-slate-100">
                                            {serviceDesc} <span className="text-slate-500 dark:text-slate-400">({operator})</span>
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">{formatCurrency(amount)}</td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">{formatCurrency(amount)}</td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">0.00</td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">{formatCurrency(taxable)}</td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">{cgstAmt ? gstRates.cgst.toFixed(3) : '0.000'}</td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">{sgstAmt ? gstRates.sgst.toFixed(3) : '0.000'}</td>
                                        <td className="px-3 py-3 text-sm text-right text-slate-700 dark:text-slate-300">0.000</td>
                                        <td className="px-3 py-3 text-sm text-right font-semibold text-slate-900 dark:text-slate-100">{formatCurrency(totalAmt)}</td>
                                    </Tr>
                                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                                        <td colSpan={12} className="px-3 py-3 text-right text-sm font-semibold text-slate-900 dark:text-slate-100">
                                            Grand Total
                                        </td>
                                        <td className="px-3 py-3 text-right text-sm font-bold text-slate-900 dark:text-slate-100">
                                            {formatCurrency(totalAmt)}
                                        </td>
                                    </tr>
                                </TBody>
                            </Table>
                        </div>
                    </Card>

                    {/* Amount in words + Remarks */}
                    <Card className="p-4 mb-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            <p className="text-sm text-slate-700 dark:text-slate-300">
                                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Amount in words: </span>
                                {numberToWords(totalAmt)}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Remarks: <span className="font-medium text-slate-700 dark:text-slate-300">{operator}</span>
                            </p>
                        </div>
                    </Card>

                    <div className="flex justify-end mb-6">
                        <div className="text-right border-t border-slate-200 dark:border-slate-800 pt-3 min-w-[200px]">
                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{companyDetails.name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Authorised Signatory</p>
                        </div>
                    </div>

                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                        This is a computer generated invoice. For any queries contact {companyDetails.email}.
                    </p>
                </>
            )}
        </>
    );
}

export default InvoicePage;